import { useState } from "react";

import {
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  MenuItem,
  Select,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { Days } from "assets/constants/Days";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import { forwardRef } from "react";
import studentAPIService from "services/studentAPI-service";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function AddStudentSchedule(props) {
  const { open, onClose, studentId, studentName } = props;
  const today = new Date()
    .toLocaleDateString("en-GB")
    .split("/")
    .reverse()
    .join("-");

  const [processing, setProcessing] = useState(false);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarColor, setSnackBarColor] = useState("success");
  const [snackBarIcon, setSnackBarIcon] = useState("");
  const [snackBarTitle, setSnackBarTitle] = useState("");
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const openSnackBar = () => setSnackBarOpen(true);
  const closeSnackBar = () => setSnackBarOpen(false);

  const initialState = {
    student: "",
    startTime: "",
    endTime: "",
    day: "",
  };

  const [inputs, setInputs] = useState({
    ...initialState,
  });

  const [errors, setErrors] = useState({
    student: false,
    startTime: false,
    endTime: false,
    day: false,
  });

  const handleCancel = () => {
    onClose("Close");
    setInputs({ ...initialState });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if everything has been input
    if (!inputs.startTime) {
      setErrors({ ...errors, startTime: true });
      return;
    } else {
      setErrors({ ...errors, startTime: false });
    }

    if (!inputs.endTime) {
      setErrors({ ...errors, endTime: true });
      return;
    } else {
      setErrors({ ...errors, endTime: false });
    }

    if (!inputs.day) {
      setErrors({ ...errors, day: true });
      return;
    } else {
      setErrors({ ...errors, day: false });
    }

    // Now that everything is ok
    setProcessing(true);

    try {
      let payload = {
        student: studentId,
        start_time: inputs.startTime,
        end_time: inputs.endTime,
        day: inputs.day,
      };

      const response = await studentAPIService.addStudentSchedule(payload);
      setProcessing(false);
      setInputs({ ...initialState });
      onClose("success");
    } catch (error) {
      setSnackBarColor("error");
      setSnackBarIcon("warning");
      setSnackBarTitle("Error");
      setSnackBarMessage("Failed to add the Schedule Record.");
      openSnackBar();
      setProcessing(false);
    }
  };

  const changeHandler = (e) => {
    setInputs({
      ...inputs,
      [e.target.name]: e.target.value,
    });
  };

  const renderSnackBar = (
    <MDSnackbar
      color={snackBarColor}
      icon={snackBarIcon}
      title={snackBarTitle}
      content={snackBarMessage}
      dateTime=""
      open={snackBarOpen}
      onClose={closeSnackBar}
      close={closeSnackBar}
      bgWhite
    />
  );

  return (
    <Dialog
      TransitionComponent={Transition}
      open={open}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        <MDTypography color="info" fontWeight="bold">
          Add Schedule
        </MDTypography>
        <MDButton iconOnly onClick={handleCancel} disabled={processing}>
          <Icon fontSize="large">cancel</Icon>
        </MDButton>
      </DialogTitle>

      <DialogContent>
        <MDBox
          variant="contained"
          component="form"
          role="form"
          method="POST"
          onSubmit={handleSubmit}
          mt={2}
        >
          <MDBox
            mb={2}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            width="100%"
          >
            <MDTypography variant="h7" color="text" fontWeight="bold">
              Student
            </MDTypography>
            <MDInput
              value={studentName}
              name="studentName"
              readOnly
              fullWidth
            />
          </MDBox>

          <MDBox
            mb={2}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            width="100%"
          >
            <MDTypography variant="h7" color="text" fontWeight="bold">
              Day
            </MDTypography>
            <Select
              id="daySelector"
              value={inputs.day}
              name="day"
              onChange={changeHandler}
              fullWidth
              sx={{
                "& .MuiSelect-icon": {
                  display: "flex",
                },
                height: "2.8rem",
              }}
              error={errors.day}
            >
              {Days.map((day, index) => (
                <MenuItem key={day.id} value={day.id}>
                  {day.name}
                </MenuItem>
              ))}
            </Select>
          </MDBox>

          <MDBox display="flex" flexDirection="row" columnGap={2}>
            <MDBox
              mb={2}
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              width="100%"
            >
              <MDTypography variant="h7" color="text" fontWeight="bold">
                Start Time
              </MDTypography>
              <MDInput
                type="time"
                value={inputs.startTime}
                name="startTime"
                onChange={changeHandler}
                error={errors.startTime}
                fullWidth
              />
            </MDBox>
            <MDBox
              mb={2}
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              width="100%"
            >
              <MDTypography variant="h7" color="text" fontWeight="bold">
                End Time
              </MDTypography>
              <MDInput
                type="time"
                value={inputs.endTime}
                name="endTime"
                onChange={changeHandler}
                error={errors.endTime}
                fullWidth
              />
            </MDBox>
          </MDBox>
        </MDBox>
        <DialogActions>
          {/* Form Here */}
          <MDButton
            variant="contained"
            color="light"
            onClick={handleCancel}
            disabled={processing}
          >
            No
          </MDButton>
          <MDButton
            variant="contained"
            color="info"
            onClick={handleSubmit}
            disabled={processing}
          >
            Submit{" "}
            {processing && (
              <CircularProgress
                size={14}
                sx={{ marginLeft: 1 }}
                color="warning"
              />
            )}
          </MDButton>
        </DialogActions>
      </DialogContent>
      {renderSnackBar}
    </Dialog>
  );
}

export default AddStudentSchedule;
